import _ from 'lodash';
import { ADMIN_CONTACT_PARTNER_ADMIN_ROLE } from '../../constants/admin.contacts.constants';
import { DOSSIER_TYPES, STAKEHOLDER_CASETAFEL_REPRESENTATIVE } from '../../constants/dossier.constants';
import { DOSSIER_DETAIL_TAB_PATHS } from '../../constants/paths.constants';
import { PERMISSIONS } from '../../constants/permissions.constants';
import { DOSSIER_ACTIE_STATUSES } from '../../constants/settings.constants';
import { hasPermission, hasPermissions } from '../../selectors/auth.selectors';

const canAddCasetafel = (user) => {
  return hasPermission(user, PERMISSIONS.STAD_DISPATCH);
};

const canAddCasetafelReport = (user, involved) => {
  const permissions = user?.permissions;
  const isRepresentative = !!involved?.find(
    (i) => i?.role === STAKEHOLDER_CASETAFEL_REPRESENTATIVE && i?.id === user.contactId
  );
  return isRepresentative || hasPermissions(permissions, [PERMISSIONS.STAD_DISPATCH]);
};

const canDeleteCasetafelReport = canAddCasetafelReport;

const isCasetafelRepresentative = (user, involved) => {
  const isRepresentative = !!involved?.find(
    (i) => i?.role === STAKEHOLDER_CASETAFEL_REPRESENTATIVE && i?.id === user.contactId
  );
  return isRepresentative;
};

const canApproveCasetafel = (user, involved) => {
  const permissions = user?.permissions;
  const isStakeholder = !!involved?.find((i) => i?.id === user.contactId);
  return isStakeholder || hasPermissions(permissions, [PERMISSIONS.STAD_DISPATCH]);
};

const canExportCasetafelAnswers = (user, involved, answers) => {
  const permissions = user?.permissions;
  const isRepresentative = !!involved?.find(
    (i) => i?.role === STAKEHOLDER_CASETAFEL_REPRESENTATIVE && i?.id === user.contactId
  );
  return answers?.length > 0 && (isRepresentative || hasPermissions(permissions, [PERMISSIONS.STAD_DISPATCH]));
};

const isPartnerEmployee = (user) =>
  hasPermissions(user?.permissions, [
    PERMISSIONS.PARTNER_ADMIN,
    PERMISSIONS.PARTNER_MEDEWERKER,
    PERMISSIONS.PARTNER_NIET_FREQUENT
  ]);

const isCityEmployee = (user) => hasPermissions(user?.permissions, [PERMISSIONS.STAD_DISPATCH, PERMISSIONS.STAD_ADMIN]);

const isSchoolEmployee = (user) =>
  hasPermissions(user?.permissions, [
    PERMISSIONS.SCHOOL_ADMIN,
    PERMISSIONS.SCHOOL_MEDEWERKER,
    PERMISSIONS.SCHOOL_NET_ADMIN,
    PERMISSIONS.SCHOOL_NET_BEGELEIDER
  ]);

const _getAllDossierStakeholders = (dossier) => {
  return [
    ..._.flatten(dossier?.requestClarification?.map((rc) => rc?.involved ?? []) ?? []),
    ..._.flatten(dossier?.caseTable?.map((ct) => ct?.involved ?? []) ?? []),
    ..._.flatten(dossier?.contactMoment?.map((ct) => ct?.involved ?? []) ?? []),
    ...(dossier?.procedure?.involved ?? []),
    ...(dossier?.assigned?.users ?? []),
    dossier?.request?.submittedBy
  ].filter((x) => !!x);
};

const isDossierStakeholder = (user, dossier) => {
  const involved = _getAllDossierStakeholders(dossier);
  return !!involved?.find((i) => i?.id === user.contactId);
};

const userFromSameOrganizationIsStakeholder = (user, dossier) => {
  const involved = _getAllDossierStakeholders(dossier);
  const userOrgsThatIsAdminOf =
    user?.permissionsWithTreepaths
      ?.filter((p) => p.role === ADMIN_CONTACT_PARTNER_ADMIN_ROLE)
      ?.map((p) => p.treePath) ?? [];

  return !!involved?.find((i) => userOrgsThatIsAdminOf.includes(i?.organisation));
};

const isStakeholderInTab = (user, tabInvolved = []) => {
  return !!tabInvolved?.find((i) => i?.id === user?.contactId);
};

const canAccessProcedureActieDossierTab = (dossier) => {
  const isProcedureEmpty =
    !dossier?.procedure?.startDate &&
    !dossier?.procedure?.endDate &&
    !dossier?.procedure?.involved?.length &&
    !dossier?.procedure?.goals?.length &&
    !dossier?.procedure?.parts?.length;

  const hasPreProcedureStatus = [
    DOSSIER_ACTIE_STATUSES.NEW_ACTION.name,
    DOSSIER_ACTIE_STATUSES.ACTION_ONGOING.name
  ].includes(dossier?.status);
  if (hasPreProcedureStatus && isProcedureEmpty) {
    return false;
  }

  const isOnHoldOrClosed = [DOSSIER_ACTIE_STATUSES.ON_HOLD.name, DOSSIER_ACTIE_STATUSES.CLOSED.name].includes(
    dossier?.status
  );
  if (isOnHoldOrClosed && isProcedureEmpty) {
    return false;
  }

  return true;
};

const canAccessDossierTab = (user, dossier, tab) => {
  if (dossier?.type === DOSSIER_TYPES.ACTION && tab.path === DOSSIER_DETAIL_TAB_PATHS.PROCEDURE.path) {
    if (!canAccessProcedureActieDossierTab(dossier)) {
      return false;
    }
  }

  const isStakeholder = isDossierStakeholder(user, dossier);

  if (isCityEmployee(user)) {
    if (!hasPermission(user, PERMISSIONS.STAD_DISPATCH) && dossier?.isPrivate) {
      return isStakeholder;
    }
    return true;
  }

  if (hasPermissions(user?.permissions, [PERMISSIONS.PARTNER_ADMIN, PERMISSIONS.PARTNER_MEDEWERKER])) {
    if (tab.path === DOSSIER_DETAIL_TAB_PATHS.OVERVIEW.path) {
      return true;
    }

    return isStakeholder || userFromSameOrganizationIsStakeholder(user, dossier);
  }

  if (tab.path === DOSSIER_DETAIL_TAB_PATHS.ATTACHMENTS.path && !(isCityEmployee(user) || isStakeholder)) {
    return false;
  }
  return true;
};

const canSeeDossier = (user, dossier) => {
  return hasPermission(user, PERMISSIONS.STAD_DISPATCH) || isDossierStakeholder(user, dossier) || !dossier?.isPrivate;
};

const canSeeAllDossiers = (user) => {
  return hasPermissions(user?.permissions, [
    PERMISSIONS.STAD_DISPATCH,
    PERMISSIONS.STAD_ADMIN,
    PERMISSIONS.PARTNER_ADMIN,
    PERMISSIONS.PARTNER_MEDEWERKER
  ]);
};

const canSeeDossierSidebar = (user) => {
  return hasPermissions(user?.permissions, [
    PERMISSIONS.STAD_DISPATCH,
    PERMISSIONS.STAD_ADMIN,
    PERMISSIONS.PARTNER_ADMIN,
    PERMISSIONS.PARTNER_MEDEWERKER,
    PERMISSIONS.PARTNER_NIET_FREQUENT
  ]);
};

const canExportDossiersCsv = (user) => {
  return hasPermissions(user?.permissions, [PERMISSIONS.STAD_ADMIN, PERMISSIONS.STAD_DISPATCH]);
};

const permissionsService = {
  canAddCasetafel,
  canAddCasetafelReport,
  canDeleteCasetafelReport,
  isCasetafelRepresentative,
  canApproveCasetafel,
  isCityEmployee,
  isSchoolEmployee,
  isPartnerEmployee,
  isDossierStakeholder,
  isStakeholderInTab,
  canExportCasetafelAnswers,
  canAccessDossierTab,
  canSeeDossier,
  canSeeAllDossiers,
  canSeeDossierSidebar,
  canExportDossiersCsv
};

export default permissionsService;
